class TeaCodeAnimation extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.config = null;
    this.elements = {};
    this.resizeHandler = this.handleResize.bind(this);
  }

  connectedCallback() {
    window.addEventListener('resize', this.resizeHandler);
    requestAnimationFrame(() => {
      this.initializeElements();
      this.loadConfiguration();
      this.getSettings();

      // Initialize Modal
      if (typeof TeaCodeModal !== 'undefined') {
        this.modalInstance = new TeaCodeModal(this);
      } else {
        console.warn('TeaCodeModal class not defined');
      }

      if (this.config && this.elements.rLetter) {
        // console.log('config', this.config);

        this.start();
      }
    });
  }

  disconnectedCallback() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }

    if (this.modalInstance && typeof this.modalInstance.destroy === 'function') {
      this.modalInstance.destroy();
    }
  }

  initializeElements() {
    const stageId = this.getAttribute('stage-id');
    const typeId = this.getAttribute('type-label-id');
    const aromaId = this.getAttribute('aroma-label-id');

    const stage = document.getElementById(stageId);
    if (!stage) {
      console.warn('Tea code animation: Stage element not found:', stageId);
      return;
    }

    this.elements = {
      stage,
      rLetter: stage.querySelector('[data-roller="letter"] .tea-code__roller__inner'),
      rStatus: stage.querySelector('[data-roller="status"] .tea-code__roller__inner'),
      rIndex: stage.querySelector('[data-roller="index"] .tea-code__roller__inner'),
      lType: document.getElementById(typeId),
      lAroma: document.getElementById(aromaId),
    };

    if (!this.elements.rLetter || !this.elements.rStatus || !this.elements.rIndex) {
      console.warn('Tea code animation: Some roller elements not found');
    }
  }

  loadConfiguration() {
    const configId = this.getAttribute('config-id');
    const configElement = document.getElementById(configId);

    if (configElement) {
      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (e) {
        console.error('Failed to parse tea code configuration:', e);
      }
    }
  }

  calculateRollerHeight() {
    if (this.elements.stage) {
      const style = getComputedStyle(this.elements.stage);
      const heightVal = style.getPropertyValue('--roller-height').trim();
      this.rollerHeight = parseInt(heightVal, 10) || 124;
    } else {
      this.rollerHeight = 124;
    }
    return this.rollerHeight;
  }

  getAnimationHeight() {
    return this.calculateRollerHeight();
  }

  getSettings() {
    this.typingSpeed = parseInt(this.getAttribute('typing-speed')) || 30;
    this.stepDelay = parseInt(this.getAttribute('step-delay')) || 2500;
    this.erasingSpeed = Math.floor(this.typingSpeed * 0.6) || 20;
  }

  typewriterEffect(element, text, speed) {
    return new Promise((resolve) => {
      element.textContent = '';
      let index = 0;
      const finalSpeed = speed || this.typingSpeed;

      const type = () => {
        if (index < text.length) {
          element.textContent += text.charAt(index);
          index++;
          setTimeout(type, finalSpeed);
        } else {
          resolve();
        }
      };

      type();
    });
  }

  reverseTypewriterEffect(element, speed) {
    return new Promise((resolve) => {
      const text = element.textContent;
      let length = text.length;
      const finalSpeed = speed || this.erasingSpeed;

      const erase = () => {
        if (length > 0) {
          element.textContent = text.substring(0, length - 1);
          length--;
          setTimeout(erase, finalSpeed);
        } else {
          resolve();
        }
      };

      erase();
    });
  }

  async update() {
    if (!this.config || !this.elements.rLetter) return;

    const data = this.config.steps[this.currentStep];
    const h = this.getAnimationHeight();

    const letterPos = this.config.map.letters.indexOf(data.letter);
    const statusPos = this.config.map.statuses.indexOf(data.status);
    const indexPos = this.config.map.indices.indexOf(data.index.toString());

    const nextStep = (this.currentStep + 1) % this.config.steps.length;
    const isLooping = nextStep === 0 && this.currentStep === this.config.steps.length - 1;

    this.elements.rLetter.style.transform = `translateY(-${letterPos * h}px)`;
    this.elements.rStatus.style.transform = `translateY(-${statusPos * h}px)`;
    this.elements.rIndex.style.transform = `translateY(-${indexPos * h}px)`;

    if (this.elements.lType || this.elements.lAroma) {
      const typeParent = this.elements.lType ? this.elements.lType.parentElement : null;
      const aromaParent = this.elements.lAroma ? this.elements.lAroma.parentElement : null;

      const updateType = this.elements.lType && this.elements.lType.textContent.trim() !== data.label;
      const updateAroma = this.elements.lAroma && this.elements.lAroma.textContent.trim() !== data.aroma_label;

      const updateIndex = this.lastIndexValue !== data.index;
      if (updateIndex) {
        this.lastIndexValue = data.index;
      }

      if (updateType || updateAroma) {
        if (updateType && typeParent) {
          typeParent.classList.add('is-animating');
          typeParent.style.width = `${typeParent.offsetWidth}px`;
        }
        if (updateAroma && aromaParent) {
          aromaParent.classList.add('is-animating');
          aromaParent.style.width = `${aromaParent.offsetWidth}px`;
        }

        const erasePromises = [];
        if (updateType) erasePromises.push(this.reverseTypewriterEffect(this.elements.lType));
        if (updateAroma) erasePromises.push(this.reverseTypewriterEffect(this.elements.lAroma));

        await Promise.all(erasePromises);

        await new Promise((resolve) => setTimeout(resolve, 500));

        if (updateType && typeParent) typeParent.style.width = 'auto';
        if (updateAroma && aromaParent) aromaParent.style.width = 'auto';
        if (updateType) {
          this.elements.lType.textContent = data.label;
        }
        if (updateAroma) {
          this.elements.lAroma.textContent = data.aroma_label;
        }

        let newTypeWidth, newAromaWidth;
        if (updateType && typeParent) newTypeWidth = typeParent.getBoundingClientRect().width;
        if (updateAroma && aromaParent) newAromaWidth = aromaParent.getBoundingClientRect().width;
        if (updateType && typeParent) typeParent.style.width = `${newTypeWidth}px`;
        if (updateAroma && aromaParent) aromaParent.style.width = `${newAromaWidth}px`;

        if (updateType) {
          this.elements.lType.textContent = '';
        }
        if (updateAroma) {
          this.elements.lAroma.textContent = '';
        }

        const typePromises = [];
        if (updateType) typePromises.push(this.typewriterEffect(this.elements.lType, data.label));
        if (updateAroma) typePromises.push(this.typewriterEffect(this.elements.lAroma, data.aroma_label));

        await Promise.all(typePromises);

        if (updateType && typeParent) {
          typeParent.classList.remove('is-animating');
          setTimeout(() => {
            typeParent.style.width = 'auto';
          }, 100);
        }
        if (updateAroma && aromaParent) {
          aromaParent.classList.remove('is-animating');
          setTimeout(() => {
            aromaParent.style.width = 'auto';
          }, 100);
        }

        if (updateType && typeParent) {
          setTimeout(() => {
            typeParent.style.width = 'auto';
          }, 100);
        }
        if (updateAroma && aromaParent) {
          setTimeout(() => {
            aromaParent.style.width = 'auto';
          }, 100);
        }
      }
    }

    this.currentStep = nextStep;

    if (isLooping) {
      setTimeout(() => {
        this.elements.rLetter.style.transition = 'none';
        this.elements.rStatus.style.transition = 'none';
        this.elements.rIndex.style.transition = 'none';

        const firstData = this.config.steps[0];
        const firstLetterPos = this.config.map.letters.indexOf(firstData.letter);
        const firstStatusPos = this.config.map.statuses.indexOf(firstData.status);
        const firstIndexPos = this.config.map.indices.indexOf(firstData.index.toString());

        this.elements.rLetter.style.transform = `translateY(-${firstLetterPos * h}px)`;
        this.elements.rStatus.style.transform = `translateY(-${statusPos * h}px)`;
        this.elements.rIndex.style.transform = `translateY(-${firstIndexPos * h}px)`;

        requestAnimationFrame(() => {
          this.elements.rLetter.style.transition = '';
          this.elements.rStatus.style.transition = '';
          this.elements.rIndex.style.transition = '';
        });
      }, this.stepDelay - 100);
    }

    this.animationTimer = setTimeout(() => this.update(), this.stepDelay);
  }

  handleResize() {
    this.calculateRollerHeight();

    if (this.elements.lType && this.elements.lType.parentElement) {
      this.elements.lType.parentElement.style.width = 'auto';
    }
    if (this.elements.lAroma && this.elements.lAroma.parentElement) {
      this.elements.lAroma.parentElement.style.width = 'auto';
    }

    if (this.config && this.config.steps && this.config.steps[this.currentStep]) {
      const data = this.config.steps[this.currentStep];
      const h = this.rollerHeight;

      const letterPos = this.config.map.letters.indexOf(data.letter);
      const statusPos = this.config.map.statuses.indexOf(data.status);
      const indexPos = this.config.map.indices.indexOf(data.index.toString());

      this.elements.rLetter.style.transition = 'none';
      this.elements.rStatus.style.transition = 'none';
      this.elements.rIndex.style.transition = 'none';

      this.elements.rLetter.style.transform = `translateY(-${letterPos * h}px)`;
      this.elements.rStatus.style.transform = `translateY(-${statusPos * h}px)`;
      this.elements.rIndex.style.transform = `translateY(-${indexPos * h}px)`;

      requestAnimationFrame(() => {
        this.elements.rLetter.style.transition = '';
        this.elements.rStatus.style.transition = '';
        this.elements.rIndex.style.transition = '';
      });
    }
  }

  start() {
    this.update();
  }

  stop() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', registerElement);
} else {
  registerElement();
}

function registerElement() {
  if (!customElements.get('tea-code-animation')) {
    customElements.define('tea-code-animation', TeaCodeAnimation);
  }
}
